/**
 * <DataTable> — shared list primitive for every Self-Serve module
 * (SELFSERVE_MODULE_SPEC §17.5.i).
 *
 * Responsibilities:
 *   - Render a typed column config
 *   - Server-side pagination (controlled offset/limit/total)
 *   - Per-row actions slot (right-aligned)
 *   - Skeleton loading state
 *   - Friendly empty state
 *   - Optional CSV export of currently visible rows
 *
 * NOT in v1 (deliberately deferred to keep the surface small):
 *   - Server-side sort     — pass sort props through `extraToolbar` for now
 *   - Inline filter pills  — supply via `extraToolbar`
 */
import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { ChevronLeft, ChevronRight, Download } from 'lucide-react';

const _csvCell = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const DataTable = ({
  columns,
  rows = [],
  total = 0,
  limit = 50,
  offset = 0,
  onPageChange,
  loading = false,
  emptyTitle = 'Nothing here yet',
  emptyHint = 'New items will appear here when created.',
  emptyAction,
  rowKey = (r) => r.id,
  rowActions,
  extraToolbar,
  exportFilename,
  testId = 'data-table',
}) => {
  const hasNext = offset + rows.length < total;
  const hasPrev = offset > 0;
  const showingFrom = total === 0 ? 0 : offset + 1;
  const showingTo = Math.min(offset + rows.length, total);

  const handleExport = () => {
    const header = columns.map((c) => c.header).join(',');
    const body = rows
      .map((r) => columns.map((c) => _csvCell(c.accessor(r))).join(','))
      .join('\n');
    const blob = new Blob([`${header}\n${body}`], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = exportFilename || 'export.csv';
    a.click();
    URL.revokeObjectURL(a.href);
  };

  return (
    <div className="space-y-3" data-testid={testId}>
      {(extraToolbar || exportFilename) && (
        <div className="flex items-center gap-2 justify-between">
          <div className="flex-1 flex items-center gap-2">{extraToolbar}</div>
          {exportFilename && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleExport}
              disabled={!rows.length}
              data-testid={`${testId}-export`}
            >
              <Download className="w-4 h-4 mr-1.5" />
              CSV
            </Button>
          )}
        </div>
      )}

      <div className="border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={`text-xs font-semibold uppercase tracking-wider ${c.headClassName || ''}`}>
                  {c.header}
                </TableHead>
              ))}
              {rowActions && <TableHead className="w-16 text-right">{' '}</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0
              ? Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key}>
                        <Skeleton className="h-4 w-32" />
                      </TableCell>
                    ))}
                    {rowActions && <TableCell><Skeleton className="h-4 w-12" /></TableCell>}
                  </TableRow>
                ))
              : rows.length === 0
              ? (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (rowActions ? 1 : 0)}
                    className="py-12 text-center"
                    data-testid={`${testId}-empty`}
                  >
                    <div className="font-medium text-base mb-1">{emptyTitle}</div>
                    <div className="text-sm text-muted-foreground mb-3">{emptyHint}</div>
                    {emptyAction}
                  </TableCell>
                </TableRow>
              )
              : rows.map((r) => (
                  <TableRow key={rowKey(r)} data-testid={`${testId}-row-${rowKey(r)}`}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className={c.cellClassName}>
                        {c.accessor(r)}
                      </TableCell>
                    ))}
                    {rowActions && (
                      <TableCell className="text-right">{rowActions(r)}</TableCell>
                    )}
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </div>

      {total > 0 && onPageChange && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div data-testid={`${testId}-status`}>
            Showing <span className="font-medium text-foreground">{showingFrom}</span>–
            <span className="font-medium text-foreground">{showingTo}</span> of{' '}
            <span className="font-medium text-foreground">{total.toLocaleString()}</span>
          </div>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="outline"
              disabled={!hasPrev || loading}
              onClick={() => onPageChange(Math.max(0, offset - limit))}
              data-testid={`${testId}-prev`}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={!hasNext || loading}
              onClick={() => onPageChange(offset + limit)}
              data-testid={`${testId}-next`}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;
